import { apiFetch } from "../../api/client";

// HR Head dashboard DTO types
export type HrKpisDto = {
  activeStaff: number;
  pendingOnboardings: number;
  openHrCases: number;
  trainingSessionsThisMonth: number;
};

export type HrStaffOverviewDto = {
  fullTimeStaff: number;
  partTimeStaff: number;
  contractors: number;
};

export type HrCasesDto = {
  openCases: number;
  highPriorityCases: number;
  resolvedThisMonth: number;
};

export type HrTrainingDto = {
  upcomingSessions: number;
  completedSessionsThisMonth: number;
  overdueMandatoryTraining: number;
};

export type HrDashboardResponseDto = {
  kpis: HrKpisDto;
  staffOverview: HrStaffOverviewDto;
  cases: HrCasesDto;
  training: HrTrainingDto;
};

// HR Manager workbench DTO types
export type HrManagerStaffTasksDto = {
  onboardingsToProcess: number;
  contractsToReview: number;
  roleChangesPending: number;
};

export type HrManagerCaseTasksDto = {
  newCasesAssigned: number;
  highPriorityCasesAssigned: number;
  casesNearingSlaBreaches: number;
};

export type HrManagerTrainingTasksDto = {
  staffToEnroll: number;
  trainingCompletionsToReview: number;
  overdueMandatoryTrainingFollowUps: number;
};

export type HrManagerWorkbenchResponseDto = {
  staffTasks: HrManagerStaffTasksDto;
  caseTasks: HrManagerCaseTasksDto;
  trainingTasks: HrManagerTrainingTasksDto;
};

export const HNB_STAFF_ROLE_VALUES = [
  "CEO",
  "MDCEO",
  "BUSINESS_HEAD",
  "BDO",
  "CTO",
  "ERP_HEAD",
  "LLM_HEAD",
  "IT_COMPLIANCE_HEAD",
  "DATA_ENGINEER",
  "DEVELOPER",
  "DATA_SCIENTIST",
  "IT_COMPLIANCE_OFFICER",
  "CFO",
  "ACCOUNTS_HEAD",
  "ACCOUNTS_OFFICER",
  "FINANCE_HEAD",
  "FINANCE_OFFICER",
  "HR_HEAD",
  "HR_MANAGER",
  "AUDIT_HEAD",
  "AUDITOR",
  "GRIEVANCE_HEAD",
  "GRIEVANCE_OFFICER",
  "OPERATIONS",
  "SUPPORT",
] as const;

export type HnbStaffRole = (typeof HNB_STAFF_ROLE_VALUES)[number];

export type StaffDashboardScope =
  | "GLOBAL"
  | "AREA"
  | "DISTRICT"
  | "FUNCTIONAL";

export type StaffVerificationQueueStatus =
  | "PENDING"
  | "UNDERREVIEW"
  | "APPROVED"
  | "REJECTED";

export type StaffDisputeStatus =
  | "OPEN"
  | "UNDERREVIEW"
  | "ESCALATEDAREA"
  | "ESCALATEDDISTRICT"
  | "ESCALATEDBUSINESSHEAD"
  | "ESCALATEDMD"
  | "RESOLVED";

export type HnbStaffProfileDto = {
  userId: string;
  staffId: string;
  companyEmail: string;
  fullName: string;
  role: HnbStaffRole;
  assignedAreaCode?: string | null;
  assignedDistrictCode?: string | null;
};

export type StaffDashboardSummaryDto = {
  scope: StaffDashboardScope;
  openDisputesCount: number;
  pendingVerificationCount: number;
  autoAcceptedBookingsCount: number;
  providerCancellationAlertsCount: number;
  unresolvedCashAuditCount: number;
};

export type StaffProviderVerificationItemDto = {
  providerId: string;
  providerName: string;
  businessType: string;
  city: string;
  postcode: string;
  submittedAt: string;
  status: StaffVerificationQueueStatus;
};

export type StaffDisputeQueueItemDto = {
  disputeId: string;
  bookingId: string;
  providerId: string;
  providerName: string;
  customerDisplayName: string;
  areaCode?: string | null;
  districtCode?: string | null;
  currentStatus: StaffDisputeStatus;
  createdAt: string;
  lastUpdatedAt: string;
};

export type StaffDashboardResponseDto = {
  profile: HnbStaffProfileDto;
  summary: StaffDashboardSummaryDto;
};

export type StaffVerificationQueueResponseDto = {
  items: StaffProviderVerificationItemDto[];
  total: number;
};

export type StaffDisputeQueueResponseDto = {
  items: StaffDisputeQueueItemDto[];
  total: number;
};

export type StaffDashboardSummary = StaffDashboardSummaryDto & {
  scopeLabel: string;
};

export type HnbStaffProfile = HnbStaffProfileDto & {
  roleLabel: string;
  assignmentLabel: string;
};

export type StaffProviderVerificationItem = StaffProviderVerificationItemDto & {
  statusLabel: string;
  submittedAtLabel: string;
  locationLabel: string;
};

export type StaffDisputeQueueItem = StaffDisputeQueueItemDto & {
  statusLabel: string;
  createdAtLabel: string;
  lastUpdatedAtLabel: string;
  territoryLabel: string;
};

export type StaffVerificationFilters = {
  status?: StaffVerificationQueueStatus;
  city?: string;
  query?: string;
};

export type StaffDisputeFilters = {
  status?: StaffDisputeStatus;
  areaCode?: string;
  districtCode?: string;
  query?: string;
};

export type CreateEmployeeInput = {
  fullName: string;
  companyEmail: string;
  mobileNumber: string;
  role: HnbStaffRole;
  department: string;
  designation: string;
  reportingTo: string;
  assignedAreaCode?: string;
  assignedDistrictCode?: string;
  joiningDate: string;
  employmentType: string;
  notes: string;
};

export type CreateEmployeeResponseDto = {
  employee: {
    userId: string;
    staffId: string;
    fullName: string;
    companyEmail: string;
    role: HnbStaffRole;
    department?: string | null;
    designation?: string | null;
    mustChangePassword: boolean;
    defaultPassword: string;
  };
};

type ApiEnvelope<T> = {
  success?: boolean;
  message?: string;
  data?: T;
};

function formatDateTime(
  value?: string | null,
  locale: string = "en-GB"
): string {
  if (!value) {
    return "Unknown";
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return "Unknown";
  }

  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(parsed);
}

function formatLocationLabel(
  city?: string | null,
  postcode?: string | null
): string {
  return [city, postcode].filter(Boolean).join(", ");
}

function getStaffRoleLabel(role: HnbStaffRole): string {
  switch (role) {
    case "CEO":
      return "CEO";
    case "MDCEO":
      return "MD / CEO";
    case "BUSINESS_HEAD":
      return "Business Head";
    case "BDO":
      return "Business Development Officer";
    case "CTO":
      return "CTO";
    case "ERP_HEAD":
      return "ERP Head";
    case "LLM_HEAD":
      return "LLM Head";
    case "IT_COMPLIANCE_HEAD":
      return "IT Compliance Head";
    case "DATA_ENGINEER":
      return "Data Engineer";
    case "DEVELOPER":
      return "Developer";
    case "DATA_SCIENTIST":
      return "Data Scientist";
    case "IT_COMPLIANCE_OFFICER":
      return "IT Compliance Officer";
    case "CFO":
      return "CFO";
    case "ACCOUNTS_HEAD":
      return "Accounts Head";
    case "ACCOUNTS_OFFICER":
      return "Accounts Officer";
    case "FINANCE_HEAD":
      return "Finance Head";
    case "FINANCE_OFFICER":
      return "Finance Officer";
    case "HR_HEAD":
      return "HR Head";
    case "HR_MANAGER":
      return "HR Manager";
    case "AUDIT_HEAD":
      return "Audit Head";
    case "AUDITOR":
      return "Auditor";
    case "GRIEVANCE_HEAD":
      return "Grievance Head";
    case "GRIEVANCE_OFFICER":
      return "Grievance Officer";
    case "OPERATIONS":
      return "Operations";
    case "SUPPORT":
      return "Support";
    default:
      return "HnB Staff";
  }
}

function getDashboardScopeLabel(scope: StaffDashboardScope): string {
  switch (scope) {
    case "GLOBAL":
      return "Global";
    case "AREA":
      return "Area";
    case "DISTRICT":
      return "District";
    case "FUNCTIONAL":
      return "Functional";
    default:
      return "Dashboard";
  }
}

function getVerificationStatusLabel(
  status: StaffVerificationQueueStatus
): string {
  switch (status) {
    case "PENDING":
      return "Pending";
    case "UNDERREVIEW":
      return "Under review";
    case "APPROVED":
      return "Approved";
    case "REJECTED":
      return "Rejected";
    default:
      return "Unknown";
  }
}

function getDisputeStatusLabel(status: StaffDisputeStatus): string {
  switch (status) {
    case "OPEN":
      return "Open";
    case "UNDERREVIEW":
      return "Under review";
    case "ESCALATEDAREA":
      return "Escalated to area";
    case "ESCALATEDDISTRICT":
      return "Escalated to district";
    case "ESCALATEDBUSINESSHEAD":
      return "Escalated to business head";
    case "ESCALATEDMD":
      return "Escalated to MD / CEO";
    case "RESOLVED":
      return "Resolved";
    default:
      return "Unknown";
  }
}

function buildAssignmentLabel(profile: HnbStaffProfileDto): string {
  if (profile.assignedAreaCode && profile.assignedDistrictCode) {
    return `${profile.assignedAreaCode} / ${profile.assignedDistrictCode}`;
  }

  if (profile.assignedAreaCode) {
    return profile.assignedAreaCode;
  }

  if (profile.assignedDistrictCode) {
    return profile.assignedDistrictCode;
  }

  return "Global / unassigned";
}

function buildTerritoryLabel(
  areaCode?: string | null,
  districtCode?: string | null
): string {
  if (areaCode && districtCode) {
    return `${areaCode} / ${districtCode}`;
  }

  if (areaCode) {
    return areaCode;
  }

  if (districtCode) {
    return districtCode;
  }

  return "Unassigned";
}

function unwrapApiData<T>(payload: T | ApiEnvelope<T> | null | undefined): T {
  if (
    payload &&
    typeof payload === "object" &&
    "data" in payload &&
    payload.data !== undefined
  ) {
    return payload.data;
  }

  return payload as T;
}

export function mapStaffProfileDto(dto: HnbStaffProfileDto): HnbStaffProfile {
  return {
    ...dto,
    roleLabel: getStaffRoleLabel(dto.role),
    assignmentLabel: buildAssignmentLabel(dto),
  };
}

export function mapStaffDashboardSummaryDto(
  dto: StaffDashboardSummaryDto
): StaffDashboardSummary {
  return {
    ...dto,
    scopeLabel: getDashboardScopeLabel(dto.scope),
  };
}

export function mapStaffVerificationItemDto(
  dto: StaffProviderVerificationItemDto
): StaffProviderVerificationItem {
  return {
    ...dto,
    statusLabel: getVerificationStatusLabel(dto.status),
    submittedAtLabel: formatDateTime(dto.submittedAt),
    locationLabel: formatLocationLabel(dto.city, dto.postcode),
  };
}

export function mapStaffDisputeItemDto(
  dto: StaffDisputeQueueItemDto
): StaffDisputeQueueItem {
  return {
    ...dto,
    statusLabel: getDisputeStatusLabel(dto.currentStatus),
    createdAtLabel: formatDateTime(dto.createdAt),
    lastUpdatedAtLabel: formatDateTime(dto.lastUpdatedAt),
    territoryLabel: buildTerritoryLabel(dto.areaCode, dto.districtCode),
  };
}

export function buildStaffVerificationQuery(
  filters: StaffVerificationFilters = {}
): string {
  const params = new URLSearchParams();

  if (filters.status) params.set("status", filters.status);
  if (filters.city) params.set("city", filters.city);
  if (filters.query) params.set("query", filters.query);

  const queryString = params.toString();
  return queryString ? `?${queryString}` : "";
}

export function buildStaffDisputeQuery(
  filters: StaffDisputeFilters = {}
): string {
  const params = new URLSearchParams();

  if (filters.status) params.set("status", filters.status);
  if (filters.areaCode) params.set("areaCode", filters.areaCode);
  if (filters.districtCode) params.set("districtCode", filters.districtCode);
  if (filters.query) params.set("query", filters.query);

  const queryString = params.toString();
  return queryString ? `?${queryString}` : "";
}

export async function fetchStaffDashboard(): Promise<{
  profile: HnbStaffProfile;
  summary: StaffDashboardSummary;
}> {
  const payload = await apiFetch<
    StaffDashboardResponseDto | ApiEnvelope<StaffDashboardResponseDto>
  >("/staff/dashboard", {
    method: "GET",
  });

  const data = unwrapApiData(payload);

  return {
    profile: mapStaffProfileDto(data.profile),
    summary: mapStaffDashboardSummaryDto(data.summary),
  };
}

export async function fetchStaffVerificationQueue(
  filters: StaffVerificationFilters = {}
): Promise<{
  items: StaffProviderVerificationItem[];
  total: number;
}> {
  const query = buildStaffVerificationQuery(filters);

  const payload = await apiFetch<
    StaffVerificationQueueResponseDto | ApiEnvelope<StaffVerificationQueueResponseDto>
  >(`/staff/provider-verification${query}`, {
    method: "GET",
  });

  const data = unwrapApiData(payload);

  return {
    items: (data.items ?? []).map(mapStaffVerificationItemDto),
    total: data.total ?? 0,
  };
}

export async function fetchStaffDisputeQueue(
  filters: StaffDisputeFilters = {}
): Promise<{
  items: StaffDisputeQueueItem[];
  total: number;
}> {
  const query = buildStaffDisputeQuery(filters);

  const payload = await apiFetch<
    StaffDisputeQueueResponseDto | ApiEnvelope<StaffDisputeQueueResponseDto>
  >(`/staff/disputes${query}`, {
    method: "GET",
  });

  const data = unwrapApiData(payload);

  return {
    items: (data.items ?? []).map(mapStaffDisputeItemDto),
    total: data.total ?? 0,
  };
}

export async function createEmployee(
  input: CreateEmployeeInput
): Promise<CreateEmployeeResponseDto["employee"]> {
  const payload = await apiFetch<
    CreateEmployeeResponseDto | ApiEnvelope<CreateEmployeeResponseDto>
  >("/staff/employees", {
    method: "POST",
    body: JSON.stringify(input),
  });

  const data = unwrapApiData(payload);

  return data.employee;
}